import { useMemo, useState } from "react";
import {
    CopyOutlined,
    MailOutlined,
    EditOutlined,
    DeleteOutlined,
    LoginOutlined,
    LoadingOutlined
} from "@ant-design/icons";
import { message, Tag } from "antd";
import CommonTable from "../../../global/table";
import CommonCardView from "../../../global/cardView";
import NoRecordFound from "../../../global/noRecordFound";
import { getDomain, copyToClipboard } from "../../../../utils/functions";
import EnhanceAffiliateCardView from "./enhance-affiliate-card-view";
import CommonDialog from "../../../global/dialog";
import CommonDrawer from "../../../global/drawer";
import EnhancedAffiliateDetails from "./enhanced-affiliate-details";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import LocalStorageService from "../../../../utils/localStorageServices";
import SOURCES from "../../../../utils/constants";

const EnhanceAffiliateList = ({
    affiliates,
    viewType,
    sort,
    setSort,
    isLoading,
    keyParams,
    editDeleteAdd,
    setEditDeleteAdd,
}) => {
    const [copyClipboardText, setCopyClipboardText] = useState("");
    const [loadingStates, setLoadingStates] = useState({});
    const [affiliateDetails, setAffiliateDetails] = useState({
        show: false,
        data: null
    });
    const localStorageServices = LocalStorageService.getService();

    // mutations and queries
    const queryClient = useQueryClient()
    const deleteAffiliateMutation = useGenericMutation({
        onSuccess: () => {
            setEditDeleteAdd({ del: false, id: null })
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.affiliates.lists({ ...keyParams }),
                type: "active",
            })
            message.success("Affiliate deleted successfully.")
        },
        onError: (err) => {
            console.error(err)
            message.error(err.response.error || "Error deleting affiliate")
        },
        endpoint: "/affiliate/delete",
    })

    const emailStripeOnboardMutation = useGenericMutation({
        endpoint: "affiliate/email/stripe-onboard-link",
    });

    const emailReferralLinkMutation = useGenericMutation({
        endpoint: "affiliate/email/referral-link",
    });

    const loginAffiliateMutation = useGenericMutation({
        endpoint: "admin/login-as-affiliate",
    });

    const handleCopyLink = async (code, type) => {
        const host = getDomain();
        const link = type === "onboard-link"
            ? `${host}stripeonboard?code=${code}`
            : `${host}signup?ref=${code}`;

        const success = await copyToClipboard(link);
        if (success) {
            setCopyClipboardText(code);
            message.success("Copied to clipboard");
            setTimeout(() => {
                setCopyClipboardText("");
            }, 2000);
        } else {
            message.error("Failed to copy link");
        }
    };

    const handleSendEmail = async (id, type) => {
        setLoadingStates((prev) => ({ ...prev, [id]: true }));
        try {
            if (type === "onboard-link") {
                await emailStripeOnboardMutation.mutateAsync({ id });
            } else {
                await emailReferralLinkMutation.mutateAsync({ id });
            }
            message.success("Email sent successfully");
        } catch (error) {
            console.error(error);
            message.error("Failed to send email");
        } finally {
            setLoadingStates((prev) => ({ ...prev, [id]: false }));
        }
    };

    const handleLoginAsUser = async (affiliateId) => {
        try {
            const userData = await loginAffiliateMutation.mutateAsync({ affiliateId });
            console.log(userData?.data?.user?.userStatus === SOURCES.STATUS_APPROVED)
            if (userData?.data?.user?.userStatus === SOURCES.STATUS_APPROVED) {
                localStorageServices.clearToken();
                localStorageServices.setCurrentUser(userData?.data);
                window.location.href = "/dashboard-affiliate";
            }
        } catch (error) {
            console.error("Login failed", error);
            message.error("Failed to login as affiliate");
        }
    };

    const handleAction = (action, id) => {
        if (action === "edit") {
            setEditDeleteAdd({ ...editDeleteAdd, editAdd: true, isEditable: true, id });
        } else if (action === "delete") {
            setEditDeleteAdd({ ...editDeleteAdd, del: true, id });
        }
    };

    const onConfirm = async () => {
        try {
            await deleteAffiliateMutation.mutateAsync({ id: editDeleteAdd.id })
        } catch (error) {
            console.error(error)
        }
    }

    const actions = [
        {
            label: "Login As User",
            icon: <LoginOutlined />,
            condition: (record) => record.originalStripeStatus === SOURCES.AFFILIATE_ONBOARD_STATUS_COMPLETED,
            onClick: (record) => handleLoginAsUser(record.id),
            className: "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white",
        },
        {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: (record) => handleAction("edit", record.id),
            className: "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white"
        },
        {
            label: "Delete",
            icon: <DeleteOutlined />,
            className: "border !border-red-500 !text-red-500 hover:!bg-red-500 hover:!text-white border-none hover:!border-none",
            onClick: (record) => handleAction("delete", record.id),
        },
    ];

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sortType: "name",
            render: (text, record) => (
                <p className="cursor-pointer hover:text-black">
                    {text}
                </p>
            ),
            isAvatar: true
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            sortType: "email",
        },
        {
            title: "Company",
            dataIndex: "companyName",
            key: "companyName",
            sortType: "companyName",
        },
        {
            title: "Type",
            dataIndex: "userType",
            key: "userType",
            sortType: "userType",
            render: (text) => (
                <p className="capitalize">{text}</p>
            )
        },
        {
            title: "Stripe Connected",
            dataIndex: "stripeConnectedStatus",
            key: "stripeConnectedStatus",
            isBadge: true,
        },
        {
            title: "Onboarding Link",
            dataIndex: "onboardingLink",
            key: "onboardingLink",
            render: (_, record) => (
                record?.originalStripeStatus === "progress" ? (
                    <div className="flex items-center justify-start gap-2">
                        {copyClipboardText === record.uniqueCode ? <Tag color="green">Copied</Tag> : <CopyOutlined
                            className="text-success cursor-pointer mr-2 text-lg"
                            title="Copy Link"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleCopyLink(record.uniqueCode, "onboard-link")
                            }
                            }
                        />}
                        {loadingStates[record.id] ? (
                            <LoadingOutlined className="text-success text-lg" spin />
                        ) : (
                            <MailOutlined
                                className="text-success cursor-pointer text-lg"
                                title="Email"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleSendEmail(record.id, "onboard-link")
                                }}
                            />
                        )}
                    </div>
                ) : <Tag color="red">Unavailable</Tag>
            )
        },
        {
            title: "Ref. Link",
            dataIndex: "refLink",
            key: "refLink",
            render: (_, record) => (
                record?.originalStripeStatus === SOURCES.AFFILIATE_ONBOARD_STATUS_COMPLETED ? (
                    <div className="flex items-center justify-start gap-2">
                        {copyClipboardText === record.uniqueCode ? <Tag color="green">Copied</Tag> : <CopyOutlined
                            className="text-success cursor-pointer mr-2 text-lg"
                            title="Copy Link"
                            onClick={() => handleCopyLink(record.uniqueCode, "referral-link")}
                        />}

                        {loadingStates[record.id] ? (
                            <LoadingOutlined className="text-success text-lg" spin />
                        ) : (
                            <MailOutlined
                                className="text-success cursor-pointer text-lg"
                                title="Email"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleSendEmail(record.id, "referral-link")
                                }}
                            />
                        )}
                    </div>
                ) : <Tag color="red">Unavailable</Tag>
            )
        },
    ];

    const data = useMemo(() => {
        if (!Array.isArray(affiliates)) return [];
        return affiliates.map((item) => ({
            ...item,
            key: item._id,
            id: item._id,
            originalStripeStatus: item.stripeConnectedStatus,
            stripeConnectedStatus:
                item.stripeConnectedStatus === SOURCES.AFFILIATE_ONBOARD_STATUS_PROGRESS
                    ? item.stripeConnectedStatus
                    : item.stripeConnectedStatus === SOURCES.AFFILIATE_ONBOARD_STATUS_COMPLETED
                        ? item.stripeConnectedStatus
                        : item.stripeConnectedStatus || "",
            stripeConnectedStatusBadge:
                item.stripeConnectedStatus === SOURCES.AFFILIATE_ONBOARD_STATUS_PROGRESS
                    ? { text: "Progress", color: "warning" }
                    : item.stripeConnectedStatus === SOURCES.AFFILIATE_ONBOARD_STATUS_COMPLETED
                        ? { text: "Completed", color: "success" }
                        : null,
        }));
    }, [affiliates]);

    if (!isLoading && (!data || data.length === 0)) {
        return <NoRecordFound message="No affiliates found." />;
    }

    return (
        <>
            {
                viewType === "LIST" ? (
                    <CommonTable
                        columns={columns}
                        data={data}
                        showActions={true}
                        actions={actions}
                        sort={sort}
                        setSort={setSort}
                        onRowClick={(record) =>
                            setAffiliateDetails({ show: true, data: record })
                        }
                    />
                ) : (
                    <CommonCardView>
                        {data.map((record) => (
                            <EnhanceAffiliateCardView
                                key={record.id}
                                record={record}
                                actions={actions}
                                onCardClick={(record) => setAffiliateDetails({ show: true, data: record })}
                                handleCopyLink={handleCopyLink}
                                handleSendEmail={handleSendEmail}
                                copyClipboardText={copyClipboardText}
                                loadingStates={loadingStates}
                            />
                        ))}
                    </CommonCardView>
                )
            }
            {
                editDeleteAdd.del && (
                    <CommonDialog
                        title="Delete Affiliate"
                        description="Are you sure you want to delete this affiliate?"
                        open={editDeleteAdd.del}
                        onCancel={() => setEditDeleteAdd({ ...editDeleteAdd, del: false })}
                        onOk={() => onConfirm()}
                        isConfirmButtonLoading={deleteAffiliateMutation.isPending}
                    />
                )
            }
            {
                affiliateDetails.show && (
                    <CommonDrawer
                        open={affiliateDetails.show}
                        setOpen={() => setAffiliateDetails({ show: false, data: null })}
                        title="Affiliate Details"
                        width={"1000px"}
                        children={
                            <EnhancedAffiliateDetails
                                affiliateDetail={affiliateDetails.data}
                                setShowDetail={(show) => setAffiliateDetails(prev => ({ ...prev, show }))}
                                getAffiliate={() => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.affiliates.lists({ ...keyParams }), type: "active" })}
                            />
                        }
                    />
                )
            }
        </>
    )
};

export default EnhanceAffiliateList;